import { createSelectorCreator, defaultMemoize } from 'reselect';
import { is } from 'immutable';
import { BATCH } from 'redux-batched-actions';
import { isShallowEqual, isDeepEqual } from 'app/utils/object';

/**
* Jacked from reducing boilerplate:
* http://redux.js.org/docs/recipes/ReducingBoilerplate.html
*
* @param {Object} handlers
* @returns {Function}
*/
export function createReducer(initialState, handlers) {
  return function reducer(state = initialState, action = {}) {
    if (handlers.hasOwnProperty(action.type)) {
      return handlers[action.type](state, action);
    }

    return state;
  };
}

/**
 * Jacked from reducing boilerplate:
 * http://redux.js.org/docs/recipes/ReducingBoilerplate.html
 *
 * @deprecated Use manually defined action creators instead
 * https://github.com/iknow/eikaiwa_content_frontend/pull/4213#discussion_r501564226
*/
export function makeActionCreator(type, ...argNames) {
  return function (...args) {
    const action = { type };
    argNames.forEach((arg, index) => {
      action[argNames[index]] = args[index];
    });
    return action;
  };
}

export const createImmutableEqualSelector = createSelectorCreator(
  defaultMemoize,
  is,
);

export const createShallowEqualSelector = createSelectorCreator(
  defaultMemoize,
  isShallowEqual,
);

/**
 * This is kind of like the inverse of reselect's `defaultMemoize`: it always calls the function,
 * but simply returns the previous instance of the result if they are the same according to
 * `Immutable.is`.
 *
 * This allows you to pipe the result through things that work on referential equality checks
 * instead of value equality checks, such as `connect()`'s default behavior.
 */
export const memoizeImmutableResult = (func) => createImmutableEqualSelector([func], x => x);

export const createShallowMemoize = fn => defaultMemoize(fn, isShallowEqual);
export const createDeepMemoize = fn => defaultMemoize(fn, isDeepEqual);

export const chainSelectors = (...selectors) => (state, input) =>
  selectors.reduce((nextInput, nextSelector) => nextSelector(state, nextInput), input);

/**
 * Takes a batched action created by `batchActions` from `redux-batched-actions` and returns a
 * flattened array of regular, non-batched actions.
 *
 * This is necessary because batched actions can be nested. For example:
 *
 * `dispatch(batchActions([action1, action2, batchActions([action3, action4])));`
 *
 * is valid and will dispatch the actions in order from action1-action4.
 */
export const flattenBatchedActions = (batchedAction) => {
  if (batchedAction.type !== BATCH) {
    return [batchedAction];
  } else {
    const actions = batchedAction.payload;
    return [].concat(...actions.map(flattenBatchedActions));
  }
};
